import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Loader, Card, Divider } from 'semantic-ui-react';
import _ from 'lodash';
import { compose } from 'recompose';

import DataAccess from './data-access';

import { authService } from '../../utils/auth-service';
import TabActionBar, { ViewModes } from '../../components/tab-action-bar';

import { getCampaignDiscussions } from '../../utils/jsonapi-functions/campaigns';
import LargeCard from './large-card';
import SmallCard from './small-card';
import TemporaryInfluencerCard from './temporary-influencer-card';
import AddStoryModal from '../../components/add-story-modal';
import AddInfluencerModal from '../../components/add-influencer-modal';
import DyzioEmptyState from '../../components/dyzio-empty-state';

import hasRESTClient from '../../hocs/has-rest-client';
import hasJSONAPIClient from '../../hocs/has-jsonapi-client';
import hasLocalStorageClient from '../../hocs/has-localstorage-client';

const DIVIDER = 'divider';
const INFLUENCER_SEARCH_CHANGED_DEBOUNCE_TIME = 800; // The number of milliseconds to delay.

const getViewMode = campaignSettings => {
  if (!campaignSettings) return ViewModes.cardsLarge;
  const { influencersViewMode } = campaignSettings;
  return influencersViewMode || ViewModes.cardsLarge;
};

const HELP_PAGE_URL = 'http://help.dyzio.co/dyzio-contextual-help/campaign-influencers';

class CampaignDashboardInfluencers extends Component {
  constructor(props) {
    super(props);
    const campaignSettings = props.dyzioStorage.getCampaignSettings({
      campaignId: props.params.id,
      key: props.dyzioStorage.KEYS.CAMPAIGN_VIEW_MODE,
    });
    this.state = {
      isLoading: true,
      hasFailed: false,
      influencers: [],
      viewMode: getViewMode(campaignSettings),
      temporaryInfluencers: [],
      addStoryVisible: false,

      addInfluencerModalVisible: false,
      isSearchingInfluencers: false,
      searchHasFailed: false,
      searchResults: [],

      discussionId: '',
      discussionName: '',
    };
  }

  componentDidMount = () => {
    this.loadDiscussions();
    this.getTemporaryInfluencers();
  }

  toggleAddStoryModal = (discussionId, discussionName) => {
    const { addStoryVisible } = this.state;
    this.setState({
      addStoryVisible: !addStoryVisible,
      discussionId,
      discussionName,
    });
  }

  loadDiscussions = () => {
    const { params: { id: campaignId } } = this.props;
    getCampaignDiscussions(campaignId)
      .then(discussions => {
        const influencers = [];
        _.forEach(discussions, discussion => {
          const { organisations } = discussion;
          _.forEach(organisations, org => {
            if (org.influencer) {
              influencers.push({
                discussionId: discussion.id,
                discussionName: discussion.name,
                ...org.influencer,
              });
            }
          });
        });
        this.setState({
          influencers,
          isLoading: false,
          hasFailed: false,
        });
      })
      .catch(() => {
        this.setState({
          influencers: [],
          isLoading: false,
          hasFailed: true,
        });
      });
  }

  addStory = asset => {
    console.log('[addStory]');
    console.log(JSON.stringify(asset, null, 2));
  }

  getTemporaryInfluencers = () => {
    const { params, restClient } = this.props;
    const campaignId = params.id;
    const url = `/campaigns/${campaignId}`;
    restClient.get(url)
      .then(response => {
        const campaign = response.data.data.attributes;
        this.setState({
          temporaryInfluencers: campaign.temporaryInfluencers,
        });
      })
      .catch(e => {
        console.log(e);
      });
  }

  onViewModeChanged = (event, data) => {
    const { name } = data;
    const { params: { id: campaignId }, dyzioStorage } = this.props;
    this.setState({ viewMode: name }, () => {
      const campaignSettings = dyzioStorage.getCampaignSettings({
        campaignId,
        key: dyzioStorage.KEYS.CAMPAIGN_VIEW_MODE,
      });
      const campaignUpdateOpts = {
        campaignId,
        key: dyzioStorage.KEYS.CAMPAIGN_VIEW_MODE,
        value: _.set(campaignSettings, 'influencersViewMode', name),
      };
      dyzioStorage.updateCampaignSettings(campaignUpdateOpts);
    });
  }

  getDropdownOptions = influencer => (
    [
      {
        label: 'add story',
        icon: 'circle outline',
        value: 'story',
        onClick: () => this.toggleAddStoryModal(influencer.discussionId, influencer.discussionName),
      },
      {
        type: DIVIDER,
      },
      {
        label: 'send invite',
        icon: 'mail outline',
        value: influencer.id,
        onClick: this.toggleSendInviteModal,
        disabled: !_.isString(influencer.email),
        popupText: `${influencer.name} does not have an email address.
        Update the influencer in My Influencers and try again.`,
      },
    ]
  )

  onInfluencerQueryChanged = _.debounce((event, data) => {
    const { value } = data;
    this.setState({
      isSearchingInfluencers: true,
    }, () => {
      this.searchInfluencers(value);
    });
  }, INFLUENCER_SEARCH_CHANGED_DEBOUNCE_TIME);

  searchInfluencers = searchTerm => {
    const { restClient } = this.props;
    const { app_metadata: { dyzio: { o_id: organisationId } } } = authService.getProfile();
    const request = {
      data: {
        organisationId,
        perPage: 200,
        currentPage: 1,
        search: {
          searchTerm,
        },
        sort: {
          field: 'name',
          order: 'DESC',
        },
      },
    };
    const searchUrl = encodeURIComponent(JSON.stringify(request));
    const url = `filter/${searchUrl}`;
    restClient({
      method: 'get',
      url,
      baseURL: process.env.REACT_APP_INFLUENCER_FILTER_API,
    }).then(response => {
      const { data: { body: { data: { data } } } } = response;
      this.setState({
        isSearchingInfluencers: false,
        searchResults: data,
        searchHasFailed: false,
      });
    });
  }

  toggleAddInfluencerModal = () => {
    const { addInfluencerModalVisible } = this.state;
    this.setState({
      addInfluencerModalVisible: !addInfluencerModalVisible,
      searchResults: [],
    });
  }

  handleInfluencerInvite = influencer => {
    const {
      campaign,
      params: { id: campaignId },
    } = this.props;

    const invitation = {
      data: {
        type: 'campaignInvitation',
        attributes: {
          name: influencer.name,
          role: 'INFLUENCER',
          campaignName: campaign.name,
          email: influencer.email,
          status: 'DEFERRED',
          link: `${window.location.origin}/invite/`,
          influencer: {
            type: 'influencer',
            id: influencer.id,
          },
          campaign: {
            type: 'campaigns',
            id: campaignId,
          },
          metadata: {
            socialHandle: influencer.primarySocialHandle,
            influencerId: influencer.id,
          },
        },
      },
    };

    DataAccess.createCampaignInvitation(invitation).then(() => {
      this.loadDiscussions();
    });
  }

  handleTemporaryInfluencerAddClick = (event, { value: influencer }) => {
    const { params: { id: campaignId } } = this.props;
    DataAccess.postInfluencerToDyzio(influencer)
      .then(() => {
        DataAccess.updateCampaignTemporaryInfluencers(campaignId, influencer)
          .then(temporaryInfluencers => {
            this.setState({
              temporaryInfluencers,
            });
          });
      })
      .catch(e => console.log(e));
  }

  render() {
    const {
      isLoading,
      influencers,
      viewMode,
      addStoryVisible,
      addInfluencerModalVisible,
      isSearchingInfluencers,
      searchResults,
      campaignId,
      discussionId,
      discussionName,
      temporaryInfluencers,
    } = this.state;

    if (isLoading) {
      return <Loader active size="big">Loading campaign influencers</Loader>;
    }

    const actionButtonProps = {
      content: 'Add an influencer...',
      onClick: this.toggleAddInfluencerModal,
    };

    const noInfluencers = _.isEmpty(influencers);

    return (
      <div>
        <TabActionBar
          hasActionButton
          actionButtonProps={actionButtonProps}
          isSmallCards={viewMode === ViewModes.cardsSmall}
          isLargeCards={viewMode === ViewModes.cardsLarge}
          onViewModeChanged={this.onViewModeChanged}
          helpPageUrl={HELP_PAGE_URL}
          shouldDisableButtons={noInfluencers}
        />

        {
          temporaryInfluencers && temporaryInfluencers.length > 0 &&
            <div>
              <Card.Group itemsPerRow={viewMode === ViewModes.cardsLarge ? 4 : 6}>
                {
                  _.map(temporaryInfluencers, ti => (
                    <TemporaryInfluencerCard
                      influencer={ti}
                      viewMode={viewMode}
                      onAddInfluencer={this.handleTemporaryInfluencerAddClick}
                    />
                  ))
                }
              </Card.Group>
              <Divider />
            </div>
        }

        <Card.Group itemsPerRow={viewMode === ViewModes.cardsLarge ? 4 : 6}>
          {
            _.map(influencers, influencer => {
              const dropdownOptions = this.getDropdownOptions(influencer);
              return viewMode === ViewModes.cardsLarge
                ? <LargeCard influencer={influencer} dropdownOptions={dropdownOptions} />
                : <SmallCard influencer={influencer} />;
            })
          }

        </Card.Group>

        {
          noInfluencers &&
          <DyzioEmptyState
            content={
              <div>
                <p>There are no influencers in your campaign yet.</p>
                <p>Use the button below to add your first influencer.</p>
              </div>
            }
            buttonLabel="Add an influencer..."
            onClick={this.toggleAddInfluencerModal}
          />
        }

        <AddStoryModal
          open={addStoryVisible}
          popupToggleHandler={this.toggleAddStoryModal}
          successHandler={this.addStory}
          campaignId={campaignId}
          discussionId={discussionId}
          discussionName={discussionName}
        />

        {
          addInfluencerModalVisible &&
          <AddInfluencerModal
            open
            toggleHandler={this.toggleAddInfluencerModal}
            onInfluencerQueryChanged={this.onInfluencerQueryChanged}
            isSearching={isSearchingInfluencers}
            results={searchResults}
            influencers={influencers}
            onInviteInfluencer={this.handleInfluencerInvite}
          />
        }


      </div>
    );
  }
}

CampaignDashboardInfluencers.propTypes = {
  restClient: PropTypes.func.isRequired,
  dyzioStorage: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  campaign: PropTypes.object.isRequired,
};

export default compose(
  hasRESTClient,
  hasJSONAPIClient,
  hasLocalStorageClient,
)(CampaignDashboardInfluencers);



/** WEBPACK FOOTER **
 ** ./src/containers/campaign-dashboard-influencers/index.js
 **/